import type { FC, PropsWithChildren, ReactNode } from 'react';
import { useContext, useState } from 'react';

import Error from 'app/components/Error';
import WidgetLoader from 'app/components/WidgetLoader';
import ErrorNotSupported from 'app/components/ErrorNotSupported';
import StreamLoader from 'app/components/StreamLoader';
import ConfigContext from 'app/common/ConfigContext';
import ErrorBoundary from 'app/common/ErrorBoundary';
import { useWidgetDisplayMetrics } from 'app/hooks/useWidgetDisplayMetrics';
import DisplayContext from 'app/common/DisplayContext';

type Props = PropsWithChildren & {
  status: RequestStatus;
  error: 'UNKNOWN' | 'NOT_FOUND' | null;
  loader?: ReactNode;
  firstLoadIndicatorOnly?: boolean;
  forceLoad?: boolean; // for testing purposes only
  id?: string;
} & (
    | {
        sendWidgetDisplayMetrics: true;
        data: ComposedEntityData | undefined;
      }
    | {
        sendWidgetDisplayMetrics?: false | undefined;
        data: any | undefined;
      }
  );

const LoadingContainer: FC<Props> = ({
  data,
  status,
  children,
  error,
  loader,
  forceLoad = false,
  firstLoadIndicatorOnly = false,
  sendWidgetDisplayMetrics,
  id
}) => {
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const configState = useContext(ConfigContext);
  const displayState = useContext(DisplayContext);
  const { block, entity } = configState;

  useWidgetDisplayMetrics(
    sendWidgetDisplayMetrics ? data : undefined,
    sendWidgetDisplayMetrics ? error : undefined,
    configState,
    displayState
  );

  if (error === 'NOT_FOUND' && (entity === 'series' || entity === 'tournament')) {
    return <ErrorNotSupported entity={entity} />;
  }

  if (status === 'FAILED' || error) {
    return <Error />;
  }

  if ((status === 'LOADING' && (firstLoadIndicatorOnly ? isFirstLoad : true)) || forceLoad) {
    if (block.length === 1 && block[0] === 'stream') {
      return <StreamLoader />;
    }

    return loader ? <>{loader}</> : <WidgetLoader />;
  }

  if (data) {
    if (isFirstLoad) setIsFirstLoad(false);
    return <ErrorBoundary id={id}>{children}</ErrorBoundary>;
  }

  return null;
};

LoadingContainer.displayName = 'LoadingContainer';

export default LoadingContainer;
